/**
 * usePushNotifications — one-time browser push opt-in.
 *
 * Spec (2026-06-16):
 *   - First-login prompt: ask permission once, then store subscription server-side.
 *   - Idempotent: if Notification.permission is already 'granted' AND a subscription
 *     exists, we just re-POST it to the backend (covers a fresh device).
 *   - localStorage `stellar.push.prompted` tracks per-user dismissal so we don't
 *     re-ask on every refresh after the user clicked 'Block'.
 */
import { useCallback, useEffect, useState } from 'react';
import { pushAPI } from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function serializeSubscription(sub) {
  // Convert PushSubscription to a JSON shape the backend can store.
  const json = sub.toJSON ? sub.toJSON() : sub;
  return {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    },
    expirationTime: json.expirationTime ?? null,
  };
}

const PROMPTED_KEY = 'stellar.push.prompted';

// 2026-08 — platform helpers for the mobile push prompt / install guide.
export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';

export function usePushNotifications({ userId, workspaceId, autoEnroll = true } = {}) {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState(null);

  const promptedKey = userId ? `${PROMPTED_KEY}:${userId}` : PROMPTED_KEY;
  const alreadyPrompted = (() => {
    try { return localStorage.getItem(promptedKey) === '1'; } catch { return false; }
  })();
  const markPrompted = () => {
    try { localStorage.setItem(promptedKey, '1'); } catch { /* noop */ }
  };

  const subscribe = useCallback(async () => {
    setError(null);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setError('Push notifications are not supported in this browser.');
        return false;
      }
      const perm = await Notification.requestPermission();
      setPermission(perm);
      markPrompted();
      if (perm !== 'granted') return false;

      const reg = await navigator.serviceWorker.ready;
      const { data } = await pushAPI.vapidKey();
      const appKey = urlBase64ToUint8Array(data.public_key);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appKey,
        });
      }
      await pushAPI.subscribe(serializeSubscription(sub), workspaceId);
      setSubscribed(true);
      return true;
    } catch (e) {
      setError(e?.message || 'Could not subscribe to push notifications.');
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, promptedKey]);

  // Auto-enroll: if permission is already granted (existing subscriber) re-POST
  // so a fresh device/session stays subscribed. 2026-08 — we no longer
  // auto-REQUEST permission on load (browsers require a user gesture and it
  // felt spammy); the PushPrompt banner asks explicitly with an Enable button.
  useEffect(() => {
    if (!autoEnroll || !userId) return;
    if (typeof Notification === 'undefined') return;
    let cancelled = false;
    (async () => {
      try {
        if (Notification.permission === 'granted') {
          await subscribe();   // re-syncs subscription with backend
          if (!cancelled) setSubscribed(true);
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, autoEnroll]);

  return { permission, subscribed, error, subscribe };
}

export default usePushNotifications;
